import { useEffect, useState } from 'react';

import { DesignPartnerApplicationModal } from '../components/DesignPartnerApplicationModal';
import { DesignPartnerGraphic } from '../components/DesignPartnerGraphic';
import { MarketingFooter, MarketingHeader } from '../components/MarketingChrome';
import { RequestAccessModal } from '../components/RequestAccessModal';

const programSteps = [
  { number: '01', icon: 'bi-gear-wide-connected', title: 'Choose one machine', description: 'Select one real production machine that matters enough to evaluate Route42 meaningfully.' },
  { number: '02', icon: 'bi-file-earmark-arrow-up', title: 'Bring its knowledge into Route42', description: 'Start with the manuals, history, notes, reports, images, and other information your team already has.' },
  { number: '03', icon: 'bi-chat-square-text', title: 'Use Route42 with your team', description: 'Ask questions, review Machine Memory, investigate issues, and use Route42 in the flow of real work.' },
  { number: '04', icon: 'bi-people', title: 'Work directly with us', description: 'Tell us what is useful, what is missing, and what would make Route42 more valuable in your environment.' },
  { number: '05', icon: 'bi-arrow-up-right-circle', title: 'Help shape what comes next', description: 'Your experience helps guide how Machine Intelligence evolves for manufacturing teams.' },
];

const partnerBenefits = [
  ['bi-box-arrow-in-right', 'Route42 for your selected machine', 'A focused deployment designed around one real operating environment.'],
  ['bi-compass', 'Guided onboarding', 'Hands-on support bringing the machine and its existing knowledge into Route42.'],
  ['bi-person-video3', 'Direct founder access', 'Work directly with the person building and shaping Route42.'],
  ['bi-headset', 'Priority support', 'Fast access to help while your team is using the product.'],
  ['bi-lightning-charge', 'Early capability access', 'See selected new capabilities as Route42 evolves.'],
  ['bi-signpost-split', 'Influence on product direction', 'Your real-world experience helps shape what we build next.'],
];

const partnerAsks = [
  ['One real production machine', 'A machine important enough that better knowledge would matter.'],
  ['Available machine knowledge', 'Manuals, work orders, notes, inspection history, images, or other useful context.'],
  ['An engaged internal champion', 'Someone who can help get the machine onboarded and Route42 into real use.'],
  ['Regular use during the program', 'Use Route42 naturally enough for both of us to learn what creates value.'],
  ['Candid feedback', 'Tell us what works, what does not, and what your team actually needs.'],
  ['Periodic check-ins', 'A small number of focused conversations during the initial collaboration.'],
];

const roles = [
  ['bi-tools', 'Maintenance Managers'],
  ['bi-shield-check', 'Reliability Managers'],
  ['bi-diagram-3', 'Reliability Engineers'],
  ['bi-building-gear', 'Plant Managers'],
  ['bi-gear', 'Manufacturing Engineers'],
  ['bi-bar-chart-line', 'Operations Leaders'],
];

const machineTraits = [
  'Important to production',
  'Difficult or time-consuming to troubleshoot',
  'Dependent on experienced technicians',
  'Associated with recurring issues or questions',
  'Supported by useful documentation or history',
  'Costly or disruptive when unavailable',
];

export function DesignPartnersPage() {
  const [requestOpen, setRequestOpen] = useState(false);
  const [applicationOpen, setApplicationOpen] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Design Partner Program | Route42';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <main className="r42-marketing-page r42-dp-page">
      <MarketingHeader onRequestAccess={() => setRequestOpen(true)} />

      <section className="r42-dp-hero">
        <div className="r42-dp-hero-grid">
          <div className="r42-dp-hero-copy">
            <span className="r42-marketing-eyebrow">Route42 Design Partner Program</span>
            <h1>Put Machine Intelligence to work on your factory floor.</h1>
            <p>
              Route42 is selecting a small group of manufacturers to bring one real production machine into Route42,
              use it in a live operating environment, and help shape how Machine Intelligence evolves.
            </p>
            <strong className="r42-dp-hero-proof">10 manufacturers. 10 machines. Real-world collaboration.</strong>
            <div className="r42-marketing-actions">
              <button className="r42-marketing-primary" type="button" onClick={() => setApplicationOpen(true)}>
                Apply to become a Design Partner <i className="bi bi-arrow-right" aria-hidden="true" />
              </button>
              <a className="r42-dp-text-link" href="#why-one-machine">Why one machine? <i className="bi bi-arrow-down" aria-hidden="true" /></a>
            </div>
          </div>
          <DesignPartnerGraphic />
        </div>
      </section>

      <section className="r42-dp-section r42-dp-why" id="why-one-machine">
        <div className="r42-dp-two-column">
          <div className="r42-dp-section-copy">
            <span className="r42-marketing-eyebrow">Start focused</span>
            <h2>Why we’re starting with one machine.</h2>
            <p>
              Route42 is designed to create value without requiring a plant-wide implementation project. Starting with
              one machine lets your team get into the product quickly, gives us a focused environment to learn from,
              and makes the value—or the gaps—easy to see.
            </p>
            <p>
              If Route42 earns its place in the workflow, expansion should be the result of demonstrated value rather
              than a promise made before the first machine is understood.
            </p>
          </div>
          <div className="r42-dp-value-card">
            <div><i className="bi bi-speedometer2" /><span><strong>Fast time to value</strong><small>Begin with information your team already has.</small></span></div>
            <div><i className="bi bi-bullseye" /><span><strong>Focused evaluation</strong><small>Measure usefulness around a machine that matters.</small></span></div>
            <div><i className="bi bi-chat-heart" /><span><strong>Close collaboration</strong><small>Build from real operating feedback, not assumptions.</small></span></div>
            <div><i className="bi bi-arrows-angle-expand" /><span><strong>Earn the right to expand</strong><small>Scale only after Route42 demonstrates value.</small></span></div>
          </div>
        </div>
      </section>

      <section className="r42-dp-section r42-dp-process">
        <div className="r42-dp-section-heading">
          <span className="r42-marketing-eyebrow">The process</span>
          <h2>How the Design Partner Program works.</h2>
        </div>
        <div className="r42-dp-steps">
          {programSteps.map((step) => (
            <article className="r42-dp-step" key={step.number}>
              <div className="r42-dp-step-top"><span>{step.number}</span><i className={`bi ${step.icon}`} /></div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="r42-dp-section r42-dp-exchange">
        <div className="r42-dp-exchange-column">
          <span className="r42-marketing-eyebrow">What you get</span>
          <h2>What Design Partners receive.</h2>
          <div className="r42-dp-benefit-grid">
            {partnerBenefits.map(([icon, title, description]) => (
              <article className="r42-dp-benefit" key={title}>
                <i className={`bi ${icon}`} />
                <div><h3>{title}</h3><p>{description}</p></div>
              </article>
            ))}
          </div>
        </div>
        <div className="r42-dp-exchange-column r42-dp-exchange-column--ask">
          <span className="r42-marketing-eyebrow">What we ask for</span>
          <h2>What we ask from Design Partners.</h2>
          <div className="r42-dp-check-list">
            {partnerAsks.map(([title, description]) => (
              <div key={title}>
                <i className="bi bi-check2" />
                <span><strong>{title}</strong><small>{description}</small></span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="r42-dp-section r42-dp-fit">
        <div className="r42-dp-fit-panel">
          <span className="r42-marketing-eyebrow">Who should apply</span>
          <h2>Built with the people responsible for the machine.</h2>
          <div className="r42-dp-role-grid">
            {roles.map(([icon, role]) => (
              <div key={role}><i className={`bi ${icon}`} /><span>{role}</span></div>
            ))}
          </div>
        </div>
        <div className="r42-dp-fit-panel">
          <span className="r42-marketing-eyebrow">A good starting point</span>
          <h2>What makes a good first machine?</h2>
          <ul className="r42-dp-traits">
            {machineTraits.map((trait) => <li key={trait}><i className="bi bi-check-circle-fill" />{trait}</li>)}
          </ul>
        </div>
      </section>

      <section className="r42-dp-commitment">
        <div className="r42-dp-commitment-inner">
          <div>
            <span className="r42-marketing-eyebrow">The initial commitment</span>
            <h2>One machine. One real operating environment. 90 days.</h2>
            <p>At the end, we review what your team learned, how Route42 was used, where it created value, and whether expanding makes sense.</p>
          </div>
          <div className="r42-dp-commitment-flow" aria-label="Design Partner commitment">
            <span><i className="bi bi-gear-wide-connected" /><b>One machine</b></span>
            <i className="bi bi-arrow-right" />
            <span><i className="bi bi-calendar3" /><b>90 days</b></span>
            <i className="bi bi-arrow-right" />
            <span><i className="bi bi-graph-up-arrow" /><b>Evaluate together</b></span>
          </div>
        </div>
      </section>

      <section className="r42-dp-final">
        <div className="r42-dp-final-inner">
          <div>
            <span className="r42-marketing-eyebrow">Help build what comes next</span>
            <h2>Help shape the future of Machine Intelligence.</h2>
            <p>
              We believe machines should become more understandable over time. Their history should stay accessible,
              past investigations should inform future decisions, and hard-earned knowledge should not disappear when
              people change roles or leave the plant.
            </p>
          </div>
          <button className="r42-marketing-primary" type="button" onClick={() => setApplicationOpen(true)}>
            Apply to become a Design Partner <i className="bi bi-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </section>

      <MarketingFooter />
      <RequestAccessModal open={requestOpen} onClose={() => setRequestOpen(false)} />
      <DesignPartnerApplicationModal open={applicationOpen} onClose={() => setApplicationOpen(false)} />
    </main>
  );
}
